/**
 * Small, local fetch-state model for the staff admin panel.
 *
 * Mirrors the model used by the Operator Console (Loading / Loaded /
 * Empty / Error / No Access) without importing across packages.
 *
 * Rules:
 *   - A failed request is NEVER turned into a zero / healthy value.
 *     Callers keep `data` as `null` and surface a `Failure` instead.
 *   - The user sees a short, actionable message. The technical detail
 *     (method, path, status, server error code) only goes to the console.
 *   - 403 is "No Access" (the signed-in role may not read this), not a
 *     generic error, and is not worth retrying.
 */
import { ApiError } from '../api';

export type FailureKind =
  | 'no_access'     // 403 — role or MFA does not allow this
  | 'unauthorized'  // 401 — session ended
  | 'not_found'     // 404
  | 'invalid'       // 400/409/422 — request rejected, message is safe to show
  | 'rate_limited'  // 429
  | 'server'        // 5xx
  | 'network'       // fetch itself failed
  | 'unknown';

export interface Failure {
  kind: FailureKind;
  status?: number;
  /** Safe-to-render, human message. */
  message: string;
  /** Retrying can plausibly help. */
  retryable: boolean;
}

const NO_ACCESS_BY_CODE: Record<string, string> = {
  mfa_required:
    'Two-factor sign-in is required for your role before this data is available. Sign in again and complete the two-factor step.',
  mutation_requires_owner_or_admin:
    'Your role does not have access to this. It is available to Owner and Admin accounts.',
  owner_only: 'This is available to Owner accounts only.',
};

function serverCode(e: ApiError): string | null {
  const c = e.body && typeof e.body.error === 'string' ? e.body.error : null;
  return c;
}

/** Strict text from a 4xx body, only when the server meant it for humans. */
function serverDetail(e: ApiError): string | null {
  const b = e.body;
  if (!b) return null;
  for (const v of [b.message, b.detail]) {
    if (typeof v === 'string' && v.length > 0 && v.length <= 300) return v;
  }
  return null;
}

/**
 * Classify any thrown value into a `Failure`. `what` is a short noun
 * phrase for the data ("license list") used in the message. Technical
 * detail is logged with console.warn and never put in `message`.
 */
export function failureFrom(e: unknown, what = 'this data'): Failure {
  if (e instanceof ApiError) {
    console.warn('[admin] request failed', e.message, e.status, e.body);
    const code = serverCode(e);
    switch (true) {
      case e.status === 401:
        return {
          kind: 'unauthorized', status: 401, retryable: false,
          message: 'Your session has ended. Sign in again to continue.',
        };
      case e.status === 403:
        return {
          kind: 'no_access', status: 403, retryable: false,
          message: (code && NO_ACCESS_BY_CODE[code])
            ?? `Your role does not have access to ${what}.`,
        };
      case e.status === 404:
        return {
          kind: 'not_found', status: 404, retryable: false,
          message: `We could not find ${what}. It may have been removed.`,
        };
      case e.status === 429:
        return {
          kind: 'rate_limited', status: 429, retryable: true,
          message: 'Too many requests right now. Wait a moment and try again.',
        };
      case e.status >= 500:
        return {
          kind: 'server', status: e.status, retryable: true,
          message: `We couldn't load ${what} because the server had a problem. Try again in a moment.`,
        };
      default: {
        const detail = serverDetail(e);
        return {
          kind: 'invalid', status: e.status, retryable: false,
          message: detail ?? `The request for ${what} was rejected.`,
        };
      }
    }
  }
  console.warn('[admin] request failed', e);
  if (e instanceof TypeError || (e as Error | null)?.name === 'TypeError') {
    return {
      kind: 'network', retryable: true,
      message: `We couldn't reach the server to load ${what}. Check your connection and try again.`,
    };
  }
  return {
    kind: 'unknown', retryable: true,
    message: `Something went wrong while loading ${what}. Try again.`,
  };
}

/** The message only, for toasts and inline write errors. */
export function userMessageFor(e: unknown, what = 'this'): string {
  if (e instanceof ApiError && e.status === 403) {
    const code = serverCode(e);
    return (code && NO_ACCESS_BY_CODE[code]) ?? `Your role is not allowed to do this.`;
  }
  if (e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 401 && e.status !== 404 && e.status !== 429) {
    const detail = serverDetail(e);
    if (detail) { console.warn('[admin] request rejected', e.message, e.body); return detail; }
  }
  return failureFrom(e, what).message;
}

/** Outcome of a read: exactly one of these, so callers can't mix them up. */
export type Resource<T> =
  | { state: 'loading' }
  | { state: 'ready'; data: T }
  | { state: 'error'; failure: Failure };

export async function loadResource<T>(load: () => Promise<T>, what?: string): Promise<Resource<T>> {
  try {
    return { state: 'ready', data: await load() };
  } catch (e) {
    return { state: 'error', failure: failureFrom(e, what) };
  }
}
